<script>
    import {onMount} from "svelte";
    import {getMapFeatures} from "vpap/common/geo.js";
    import {Head} from "vpap/svelte/leaflet";
    import Select from "vpap/svelte/Select.svelte";
    import Tooltip from "vpap/svelte/Tooltip.svelte";
    import ButtonPanel from "vpap/svelte/ButtonPanel.svelte";

    import ResultsMap from "./results_map";
    import {votingUnits, tooltip, activeData, currentRound} from "./stores";
    import ProgressBar from "./ProgressBar.svelte";
    import RedistributedVotes from "./RedistributedVotes.svelte";

    export let votingUnitMapUrl, votingUnitMapLayer, attorneygeneralurl, governorurl, ltgovernorurl, activeSeat;
    
    let activeRace = activeSeat ?
        activeSeat === "attorney-general" ? 2 :
        activeSeat === "lt-governor" ? 1 :
        activeSeat === "governor" ? 0 : 0 : 0;
    $: activeUrl = activeRace === 0 ? governorurl :
        activeRace === 1 ? ltgovernorurl :
        attorneygeneralurl;

    $: if (activeUrl) {
        selectedRound = 0;
        activeData.load(activeUrl);
    }

    let rounds = [];
    $: if ($activeData.rounds_overview) {
        rounds = $activeData.rounds_overview
            .map(d => `Round ${d.number}`)
    }

    let selectedRound = 0;
    $: if ($activeData.rounds_overview) {
        currentRound.set($activeData.rounds_overview[selectedRound].number)
    }
    let containerWidth;

    function sortCandidates(a, b) {
        if (a.weighted_votes < b.weighted_votes) return 1;
        else if (a.weighted_votes > b.weighted_votes) return -1;
        else {
            if (a.cand_name.toLowerCase() < b.cand_name.toLowerCase()) return -1;
            else if (a.cand_name.toLowerCase() > b.cand_name.toLowerCase()) return 1;
            else return 0;
        }
    }

    onMount(async () => {
        votingUnits.set(await getMapFeatures(votingUnitMapUrl, votingUnitMapLayer));
    })
</script>


<Head/>
<template>
    <div class="results-container" bind:clientWidth={containerWidth} style="margin: 0 auto; padding: 3px">
        <p class="sr-only">Click on one of the buttons below to choose a race to see results from</p>
        <ButtonPanel
            bind:activeSection={activeRace}
            sections={["Governor", "Lt. Governor", "Att. General"]}
        />
        {#if $activeData && $activeData.rounds_overview && $activeData.rounds_overview.length >= 1}
        <div class="round-selection">
            <Select
                id="round-picker-select"
                name="round-picker"
                bind:selected={selectedRound}
                options={rounds}
            >
                <div slot="label" class="sr-only">Select a round to view results from:</div>
            </Select>
        </div>
        <div class="elex-progress-bar section">
            <ProgressBar 
                width={Math.min(500, containerWidth)}
                percent={$activeData.rounds_overview[selectedRound].pct_reported}
            />
        </div>
        {#if $activeData.results_overview}
        <div class="overall-results section">
            <h2 class="sr-only">Results in Round {$currentRound}</h2>
            {#each $activeData.results_overview[$currentRound].sort(sortCandidates) as cand}
            <div class="candidate">
                <div 
                    aria-hidden="true"
                    class="candidate-circle" 
                    style={`opacity: ${cand.is_eliminated ? 0.8 : 1};background-color: ${cand.is_eliminated ? "#cccccc" : cand.image_color};background-image: url(${cand.image_url})`}
                ></div>
                <div
                    class="cand-result"
                    class:cand-won={cand.is_winner}
                    class:cand-eliminated={cand.is_eliminated}
                    aria-hidden="true"
                >
                </div>
                <div class="cand-result-info">
                    <div class="cand-name">{cand.cand_name}</div>
                    {#if cand.is_winner}<span class="sr-only">Winner</span>{/if}
                    {#if cand.is_eliminated}<span class="sr-only">Eliminated</span>{/if}
                    <div class="cand-pct"><span class="sr-only">Percent of weighted vote: </span>{cand.weighted_vote_pct}%</div>
                    <div class="cand-votes"><span class="sr-only">Number of weighted votes: </span>{cand.weighted_votes.toLocaleString("en")}</div>
                </div>
            </div>
            {/each}
        </div>
        {/if}
        {#if $activeData.redistributed_votes && $activeData.redistributed_votes[$currentRound]}
            <div class="results-redistributed section">
                <h2>Redistributed Votes</h2>
                <RedistributedVotes itemWidth={Math.min(containerWidth, 400)}/>
            </div>
        {/if}
        {#if $activeData.results_detail && containerWidth}
        <div class="results-map section">
            <h2>Map of Weighted Votes</h2>
            <div class="help-info">Hover over or tap on a voting unit for more details.</div>
            {#if $votingUnits}
            <ResultsMap {containerWidth} firstRound={$currentRound === 1}/>
            {/if}
        </div>
        {/if}
        {/if}
        <div class="tooltip-container">
            {#if Object.keys($tooltip.data).length > 0}
            <Tooltip
                x={$tooltip.x}
                y={$tooltip.y}
                visible={$tooltip.visible}
                isMobile={containerWidth < 500}
                id="results-tooltip"
            >
                <div class="tip-head">{$tooltip.data.unit_name}</div>
                <div class="tip-content">
                    {#if $tooltip.data.candidates.length > 0}
                    <table class="tip-table">
                        <thead>
                            <tr class="tip-table-head">
                                <th aria-hidden="true"></th>
                                <th>Candidate</th>
                                <th>{#if $tooltip.data.votes_type === "overall"}Votes{:else}Redistributed Votes{/if}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {#each $tooltip.data.candidates as cand}
                            <tr class="tip-table-row">
                                <td aria-hidden="true">
                                    <span class="table-circle" style={`background-color: ${cand.cand_color}`}></span>
                                </td>
                                <td>{cand.cand_name}</td>
                                <td>{#if cand.votes !== null}{cand.votes}{:else}N/A{/if}</td>
                            </tr>
                            {/each}
                        </tbody>
                    </table>
                    {:else}
                            <p>No votes have been recorded for this voting unit.</p>
                    {/if}
                </div>
            </Tooltip>
            {/if}
        </div>
    </div>
    
</template>

<style lang="scss">
    * {
        color: #607482;
    }
    .section {
        margin-bottom: 2.5em;
    }

    :global(#round-picker-select) {
        font-size: 1.3rem;
        padding: .2em;
        color: #607482;
    }

    .round-selection {
        margin-bottom: 20px;
    }

    .overall-results {
        display: flex;
        flex-wrap: wrap;
        font-size: 0.75em;
        text-align: center;
    }

    .candidate {
        margin-right: 4em;
        display: grid;
        grid-template-columns: 70% 30%;
        grid-template-rows: 50% 50%;
        place-items: center;
        margin-bottom: 20px;
    }

    .cand-result {
        font-weight: bold;
        margin-left: .2em;
    }

    .cand-eliminated {
        color: #d65454;
        font-size: 1.6rem;

        &:after {
            content: "\2716";
        }
    }

    .cand-won {
        color: #5fa061;
        font-size: 1.85rem;

        &::after {
            content: "\2714";
        }
    }

    .cand-result-info {
        margin-top: .3em;
        grid-column-start: 1;
        grid-column-end: 1;
    }

    .cand-pct, .cand-votes {
        font-weight: bold;
    }

    :global(#results-tooltip) {
        z-index: 2000;
    }

    .tip-head {
        font-size: 1.1rem;
        text-align: center;
        border-bottom: 1px solid #ddd;
        margin: .2em;
    }

    .tip-table {
        width: 100%;
    }

    .table-circle {
        width: 12px;
        height: 12px;
        display: inline-block;
        border-radius: 50%;
        top: 2px;
        position: relative;
    }

    h2 {
        margin-bottom: 20px;
    }
</style>